import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { AppStateContext } from './AppStateContext';
import { StreamsContext } from './StreamsContext';
import { TradingContext } from './TradingContext';
import { useTradingRulesConfig } from './TradingRulesConfigContext';
import { useTradeChecklist } from '../hooks/useTradeChecklist';
import {
  buildChecklistEntryPayload,
  DEFAULT_CHECKLIST_SETTINGS,
  DEFAULT_CHECKLIST_TEMPLATE,
  evaluateChecklistItems,
  evaluateTradeRulesForTicket,
  normalizeChecklistSettings,
  normalizeChecklistTemplate,
  resolveChecklistAttemptStatus,
} from '../utils/tradeChecklistEval';

export const TRADE_CHECKLIST_UPDATED_EVENT = 'tradeChecklist:updated';

function isChecklistPayload(res) {
  return !!(res && typeof res === 'object' && !res.error && res.template != null);
}

export const TradeChecklistContext = createContext({
  ready: false,
  visible: false,
  checklistId: null,
  checklistName: 'Default',
  template: DEFAULT_CHECKLIST_TEMPLATE,
  settings: DEFAULT_CHECKLIST_SETTINGS,
  manualValues: {},
  setManualChecked: () => {},
  checklistEval: { results: [], allPassed: true, passedCount: 0, total: 0 },
  rulesEval: { results: [], allPassed: true, passedCount: 0, total: 0 },
  blockBuySell: false,
  collapsed: true,
  setCollapsed: () => {},
  recordAttempt: async () => null,
  refetchChecklist: async () => null,
  saveChecklist: async () => null,
});

export const TradeChecklistProvider = ({ children }) => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const authToken = user?.token || null;
  const {
    getTradeChecklist,
    saveTradeChecklist,
    createTradeChecklistEntry,
    updateTradeChecklistEntry,
  } = useTradeChecklist();
  const { settings: tradingRulesSettings } = useTradingRulesConfig();
  const {
    selectedTicker,
    activeChartTicker,
    orderType,
    bracketType,
    targetingModeEnabled,
    stopLoss,
    quantity,
    limitPrice,
    chartInterval,
    chartUnit,
    sessionTemplate: appSessionTemplate,
  } = useContext(AppStateContext);
  const { barsByKey, quotesBySymbol } = useContext(StreamsContext);
  const {
    selectedAccount,
    selectedTickerDetails,
    isFutures,
  } = useContext(TradingContext);

  const [checklistId, setChecklistId] = useState(null);
  const [checklistName, setChecklistName] = useState('Default');
  const [template, setTemplate] = useState(DEFAULT_CHECKLIST_TEMPLATE);
  const [settings, setSettings] = useState(DEFAULT_CHECKLIST_SETTINGS);
  const [manualValues, setManualValues] = useState({});
  const [ready, setReady] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [panelEditing, setPanelEditing] = useState(false);
  const hasAppliedInitialCollapse = useRef(false);

  const applyChecklistResponse = useCallback((res) => {
    if (!isChecklistPayload(res)) return false;
    setChecklistId(res.id || null);
    setChecklistName(res.name || 'Default');
    setTemplate(normalizeChecklistTemplate(res.template));
    const nextSettings = normalizeChecklistSettings(res.settings);
    setSettings(nextSettings);
    if (!hasAppliedInitialCollapse.current) {
      hasAppliedInitialCollapse.current = true;
      // Always start collapsed on first load for compact trade panel UX.
      setCollapsed(true);
    }
    return true;
  }, []);

  const refetchChecklist = useCallback(async () => {
    if (!authToken) return null;
    try {
      const res = await getTradeChecklist();
      applyChecklistResponse(res);
      return res;
    } catch (_) {
      return null;
    } finally {
      setReady(true);
    }
  }, [authToken, getTradeChecklist, applyChecklistResponse]);

  // Load once auth token is available (AuthProvider hydrates async from localStorage)
  useEffect(() => {
    if (!authToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getTradeChecklist();
        if (cancelled) return;
        applyChecklistResponse(res);
      } catch (_) {
        // empty checklist is fine
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authToken, getTradeChecklist, applyChecklistResponse]);

  // Settings builder dispatches this after a successful save
  useEffect(() => {
    const onUpdated = () => {
      refetchChecklist();
    };
    window.addEventListener(TRADE_CHECKLIST_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(TRADE_CHECKLIST_UPDATED_EVENT, onUpdated);
  }, [refetchChecklist]);

  // Refetch when entering trade routes after edits elsewhere
  useEffect(() => {
    if (!authToken) return;
    const path = location.pathname || '';
    if (path.startsWith('/trade') || path.startsWith('/settings') || path.startsWith('/dashboard')) {
      refetchChecklist();
    }
  }, [location.pathname, authToken, refetchChecklist]);

  const ticker = selectedTicker || activeChartTicker;
  const sessionTemplate = appSessionTemplate || 'Default';
  const bars = useMemo(() => {
    if (!ticker || !chartInterval || !chartUnit) return [];
    const key = [ticker, chartInterval, chartUnit, sessionTemplate].join('|');
    const raw = barsByKey?.[key];
    return Array.isArray(raw) ? raw : [];
  }, [barsByKey, ticker, chartInterval, chartUnit, sessionTemplate]);

  const currentQuote = quotesBySymbol?.[ticker] || quotesBySymbol?.[selectedTicker];

  const estimatedRisk = useMemo(() => {
    if (!targetingModeEnabled || bracketType === 'none') return null;
    if (!quantity || !stopLoss) return null;
    const entryPrice = limitPrice
      ? parseFloat(limitPrice)
      : (currentQuote?.Last ? parseFloat(currentQuote.Last) : null);
    if (!entryPrice) return null;
    const stopLossPrice = parseFloat(stopLoss);
    if (!stopLossPrice || Number.isNaN(stopLossPrice)) return null;
    const distance = Math.abs(entryPrice - stopLossPrice);
    const tickMultiplier = isFutures
      ? (selectedTickerDetails?.PriceFormat?.PointValue || 1)
      : 1;
    return distance * quantity * tickMultiplier;
  }, [
    targetingModeEnabled,
    bracketType,
    quantity,
    stopLoss,
    limitPrice,
    currentQuote,
    isFutures,
    selectedTickerDetails,
  ]);

  const orderForm = useMemo(() => ({
    symbol: ticker,
    quantity,
    orderType,
    brackets: {
      stopLoss: {
        enabled: targetingModeEnabled && bracketType !== 'none',
        stopPrice: stopLoss || '',
      },
    },
    computedRisk: estimatedRisk,
  }), [
    ticker,
    quantity,
    orderType,
    targetingModeEnabled,
    bracketType,
    stopLoss,
    estimatedRisk,
  ]);

  const checklistEval = useMemo(
    () => evaluateChecklistItems(template.items, { bars, manualValues }),
    [template.items, bars, manualValues]
  );

  const rulesEval = useMemo(
    () => evaluateTradeRulesForTicket({
      settings: tradingRulesSettings,
      orderForm,
    }),
    [tradingRulesSettings, orderForm]
  );

  // Settings master switch (default on). Tracked trading rules (stop loss, max risk,
  // hours, …) still drive checklist rows via evaluateTradeRulesForTicket.
  const visible = ready && !!settings.enabled;

  const blockBuySell = !!(
    settings.block_until_complete &&
    checklistEval.total > 0 &&
    !checklistEval.allPassed
  );

  const setManualChecked = useCallback((key, checked) => {
    setManualValues((prev) => ({ ...prev, [key]: !!checked }));
  }, []);

  const recordAttempt = useCallback(async ({
    side,
    status,
    orderId = null,
    enforcementBlocked = false,
  } = {}) => {
    const resolvedStatus = status || resolveChecklistAttemptStatus({
      blockUntilComplete: settings.block_until_complete,
      checklistComplete: checklistEval.allPassed,
      enforcementBlocked,
    });

    const entry = buildChecklistEntryPayload({
      template,
      checklistEval,
      rulesEval,
      orderForm: { ...orderForm, side, symbol: ticker },
      manualValues,
    });

    const passed = checklistEval.allPassed && rulesEval.allPassed;

    try {
      return await createTradeChecklistEntry({
        checklist_id: checklistId,
        account_id: selectedAccount?.AccountID || null,
        symbol: ticker || null,
        side: side || null,
        order_type: orderType || null,
        order_id: orderId || null,
        entry,
        status: resolvedStatus,
        passed,
      });
    } catch (_) {
      return null;
    }
  }, [
    settings.block_until_complete,
    checklistEval,
    rulesEval,
    template,
    orderForm,
    manualValues,
    createTradeChecklistEntry,
    checklistId,
    selectedAccount,
    ticker,
    orderType,
  ]);

  const attachOrderId = useCallback(async (entryId, orderId) => {
    if (!entryId || !orderId) return null;
    try {
      return await updateTradeChecklistEntry(entryId, { order_id: orderId });
    } catch (_) {
      return null;
    }
  }, [updateTradeChecklistEntry]);

  const saveChecklist = useCallback(async ({
    template: nextTemplate,
    settings: nextSettings,
    name: nextName,
  } = {}) => {
    if (!authToken) return null;
    const templateToSave = normalizeChecklistTemplate(
      nextTemplate != null ? nextTemplate : template
    );
    const settingsToSave = normalizeChecklistSettings(
      nextSettings != null ? nextSettings : settings
    );
    const nameToSave = nextName || checklistName || 'Default';
    try {
      const res = await saveTradeChecklist({
        name: nameToSave,
        template: templateToSave,
        settings: settingsToSave,
      });
      if (isChecklistPayload(res)) {
        applyChecklistResponse(res);
      } else {
        setTemplate(templateToSave);
        setSettings(settingsToSave);
        setChecklistName(nameToSave);
      }
      // Do not re-dispatch TRADE_CHECKLIST_UPDATED_EVENT here — this provider
      // listens for that event to refetch, and settings builder already emits it.
      return res;
    } catch (err) {
      return { error: err?.message || 'Failed to save checklist' };
    }
  }, [
    authToken,
    saveTradeChecklist,
    template,
    settings,
    checklistName,
    applyChecklistResponse,
  ]);

  const value = useMemo(() => ({
    ready,
    visible,
    checklistId,
    checklistName,
    template,
    settings,
    manualValues,
    setManualChecked,
    checklistEval,
    rulesEval,
    blockBuySell,
    collapsed,
    setCollapsed,
    panelEditing,
    setPanelEditing,
    recordAttempt,
    attachOrderId,
    refetchChecklist,
    saveChecklist,
  }), [
    ready,
    visible,
    checklistId,
    checklistName,
    template,
    settings,
    manualValues,
    setManualChecked,
    checklistEval,
    rulesEval,
    blockBuySell,
    collapsed,
    panelEditing,
    recordAttempt,
    attachOrderId,
    refetchChecklist,
    saveChecklist,
  ]);

  return (
    <TradeChecklistContext.Provider value={value}>
      {children}
    </TradeChecklistContext.Provider>
  );
};

export const useTradeChecklistContext = () => useContext(TradeChecklistContext);
